import React, { useState } from "react";
import { toast } from "sonner";
import { X, Key, Eye, EyeOff, Check, AlertTriangle } from "lucide-react";
import api from "../lib/api";

export default function ChangePasswordModal({ open, onClose }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [show, setShow] = useState({ curr: false, next: false, conf: false });
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const reset = () => { setForm({ current_password: "", new_password: "", confirm: "" }); setShow({ curr:false, next:false, conf:false }); };
  const close = () => { reset(); onClose(); };

  const rules = {
    len: form.new_password.length >= 8,
    diff: form.new_password.length > 0 && form.new_password !== form.current_password,
    match: form.new_password.length > 0 && form.new_password === form.confirm,
  };
  const canSubmit = rules.len && rules.diff && rules.match && form.current_password.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Contraseña actualizada correctamente");
      close();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo actualizar la contraseña");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={close}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg border border-slate-200 w-full max-w-md"
        data-testid="change-password-modal"
      >
        <div className="flex items-start justify-between p-5 border-b border-slate-200">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-red-50 text-crejpp flex items-center justify-center shrink-0"><Key className="w-5 h-5" /></div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Seguridad</div>
              <h3 className="font-heading text-lg font-bold text-slate-900">Cambiar contraseña</h3>
            </div>
          </div>
          <button type="button" onClick={close} className="p-2 hover:bg-slate-100 rounded-md" data-testid="cp-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          <PwdField
            label="Contraseña actual"
            value={form.current_password}
            onChange={(v) => setForm({ ...form, current_password: v })}
            visible={show.curr}
            onToggle={() => setShow({ ...show, curr: !show.curr })}
            testid="cp-current"
            autoFocus
          />
          <PwdField
            label="Nueva contraseña"
            value={form.new_password}
            onChange={(v) => setForm({ ...form, new_password: v })}
            visible={show.next}
            onToggle={() => setShow({ ...show, next: !show.next })}
            testid="cp-new"
          />
          <PwdField
            label="Confirmar nueva contraseña"
            value={form.confirm}
            onChange={(v) => setForm({ ...form, confirm: v })}
            visible={show.conf}
            onToggle={() => setShow({ ...show, conf: !show.conf })}
            testid="cp-confirm"
          />

          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1.5">
            <Rule ok={rules.len} label="Al menos 8 caracteres" />
            <Rule ok={rules.diff} label="Distinta de la actual" />
            <Rule ok={rules.match} label="Ambos campos coinciden" />
          </div>

          <p className="text-[11px] text-slate-500 leading-relaxed">
            <AlertTriangle className="w-3 h-3 inline mr-1 text-amber-600" />
            Utilice una contraseña única y no comparta sus credenciales institucionales. El cambio quedará registrado en la bitácora de auditoría.
          </p>
        </div>

        <div className="border-t border-slate-200 p-4 flex items-center justify-end gap-2 bg-slate-50">
          <button type="button" onClick={close} className="px-4 py-2 border border-slate-300 rounded-md text-sm bg-white hover:bg-slate-100" data-testid="cp-cancel">Cancelar</button>
          <button type="submit" disabled={!canSubmit || saving} className="px-4 py-2 bg-crejpp hover:bg-crejpp-dark disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-md text-sm font-semibold inline-flex items-center gap-1.5" data-testid="cp-submit">
            <Key className="w-4 h-4" /> {saving ? "Guardando…" : "Actualizar contraseña"}
          </button>
        </div>
      </form>
    </div>
  );
}

function PwdField({ label, value, onChange, visible, onToggle, testid, autoFocus }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">{label}</span>
      <div className="relative">
        <input
          type={visible ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoFocus={autoFocus}
          data-testid={testid}
          className="w-full pl-3 pr-10 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-crejpp focus:ring-2 focus:ring-red-500/20"
          autoComplete="new-password"
        />
        <button type="button" onClick={onToggle} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700" tabIndex={-1}>
          {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </label>
  );
}

function Rule({ ok, label }) {
  return (
    <div className={`flex items-center gap-2 text-xs ${ok ? "text-emerald-700" : "text-slate-500"}`}>
      <span className={`w-4 h-4 rounded-full border flex items-center justify-center ${ok ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-300"}`}>
        {ok && <Check className="w-3 h-3" strokeWidth={3} />}
      </span>
      {label}
    </div>
  );
}
